/**
 * Maps backend HTTP status codes and error messages to i18n translation keys
 * Used by SignInForm to show appropriate user-friendly error messages
 *
 * Backend error scenarios from STO-2084:
 * - 400: Invalid channel, identifier, or locationId
 * - 403: Account suspended
 * - 503: Feature disabled or delivery failed
 *
 * COUPLING WARNING (M3):
 * 400 errors have no structured code from the backend — message text matching is the only
 * option for those. 403 and 503 do carry a structured code (e.g. CUSTFORBDN) and are mapped
 * by code first; message text is the fallback for cases where code is absent.
 *
 * Backend strings we depend on for 400 (see errorCodeMapper.test.ts for exact examples):
 * - 400 channel: "Invalid channel", "bad channel"
 * - 400 phone (E.164): "identifier must be E.164", "invalid phone", "+1"
 * - 400 email: "invalid email", "@"
 * - 400 locationId: "locationid", "location"
 * - 503 feature: "feature", "available"
 * - 503 delivery: "failed", "send", "otp"
 */

interface NormalizedError {
	status: number
	message?: string
	code?: string
}

export function getErrorMessageKey(error: NormalizedError): string {
	const { status, message = '', code } = error
	const messageLower = message.toLowerCase()

	// ===== 400 BAD REQUEST =====
	// Backend does not return a structured code for 400s — message text is the only signal.
	if (status === 400) {
		// Channel validation
		if (messageLower.includes('channel')) {
			return 'signin:errors:invalidChannel'
		}

		// Identifier validation — E.164 (phone)
		if (
			messageLower.includes('e.164') ||
			messageLower.includes('phone') ||
			/\+\d/.test(messageLower)
		) {
			return 'signin:errors:invalidPhoneFormat'
		}

		// Identifier validation — Email
		if (messageLower.includes('email') || messageLower.includes('@')) {
			return 'signin:errors:invalidEmailFormat'
		}

		// LocationId validation
		if (messageLower.includes('locationid') || messageLower.includes('location')) {
			return 'signin:errors:invalidLocationId'
		}

		// Default for other 400 errors
		return 'signin:errors:unexpectedError'
	}

	// ===== 403 FORBIDDEN =====
	// CUSTFORBDN is the only 403 code for request-otp — always means account suspended.
	// Prefer the structured code; fall back to message text when code is absent.
	if (status === 403) {
		if (
			code === 'CUSTFORBDN' ||
			messageLower.includes('suspended') ||
			messageLower.includes('forbidden')
		) {
			return 'signin:errors:accountSuspended'
		}
		return 'signin:errors:accountSuspended'
	}

	// ===== 503 SERVICE UNAVAILABLE =====
	if (status === 503) {
		// Feature flag disabled
		if (messageLower.includes('feature') || messageLower.includes('available')) {
			return 'signin:errors:featureDisabled'
		}

		// Lighthouse dispatch failed
		if (
			messageLower.includes('failed') ||
			messageLower.includes('send') ||
			messageLower.includes('otp')
		) {
			return 'signin:errors:deliveryFailed'
		}

		return 'signin:errors:featureDisabled'
	}

	// ===== NETWORK ERRORS =====
	if (!status || status === 0) {
		return 'signin:errors:networkError'
	}

	// ===== UNEXPECTED STATUS CODES =====
	return 'signin:errors:unexpectedError'
}
